// pages/dashboard/Analytics.jsx
// Full analytics dashboard for Steve
// Overview metrics, submission trends, revenue, dimension heatmap

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#1B9AAA', '#0A2342', '#27AE60', '#F39C12', '#E74C3C', '#9B59B6', '#1ABC9C', '#E67E22'];

const Analytics = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const [period, setPeriod] = useState('30d');
  const [overview, setOverview] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [dimensions, setDimensions] = useState([]);
  const [enterprise, setEnterprise] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, submissionsRes, revenueRes, breakdownRes, dimensionsRes, enterpriseRes] = await Promise.all([
        axios.get(`/analytics/overview?period=${period}`),
        axios.get(`/analytics/submissions?period=${period}`),
        axios.get(`/analytics/revenue?period=12m`),
        axios.get(`/analytics/breakdown?period=${period}`),
        axios.get('/analytics/dimensions'),
        axios.get('/analytics/enterprise'),
      ]);

      if (overviewRes.data.success) setOverview(overviewRes.data.data);
      if (submissionsRes.data.success) setSubmissions(submissionsRes.data.data.data);
      if (revenueRes.data.success) setRevenue(revenueRes.data.data.data);
      if (breakdownRes.data.success) setBreakdown(breakdownRes.data.data);
      if (dimensionsRes.data.success) setDimensions(dimensionsRes.data.data.data);
      if (enterpriseRes.data.success) setEnterprise(enterpriseRes.data.data.data);
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    { label: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
    { label: 'Submissions', path: '/admin/submissions', icon: '📋' },
    { label: 'Transactions', path: '/admin/transactions', icon: '💳' },
    { label: 'Meetings', path: '/admin/meetings', icon: '📅' },
    { label: 'Enterprise', path: '/admin/enterprise', icon: '🏢' },
    { label: 'Form Maker', path: '/admin/forms', icon: '📝' },
    { label: 'Analytics', path: '/admin/analytics', icon: '📈', active: true },
    { label: 'Settings', path: '/admin/settings', icon: '⚙️' },
  ];

  const GrowthBadge = ({ value }) => (
    <span style={{
      fontSize: '12px',
      fontWeight: '600',
      color: value >= 0 ? '#27AE60' : '#E74C3C',
      backgroundColor: value >= 0 ? '#E8F8EF' : '#FADBD8',
      padding: '2px 8px',
      borderRadius: '100px',
    }}>
      {value >= 0 ? '↑' : '↓'} {Math.abs(value)}%
    </span>
  );

  return (
    <div style={s.page}>
      <div style={s.sidebar}>
        <div style={s.sidebarLogo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
        <nav style={s.nav}>
          {navItems.map(item => (
            <button key={item.path} style={{ ...s.navItem, ...(item.active ? s.navActive : {}) }} onClick={() => navigate(item.path)}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={s.sidebarFooter}>
          <div style={s.adminName}>{admin?.name}</div>
          <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Analytics</h1>
            <p style={s.subtitle}>Business performance overview</p>
          </div>
          <div style={s.periodSelector}>
            {[
              { value: '7d', label: '7 Days' },
              { value: '30d', label: '30 Days' },
              { value: '90d', label: '90 Days' },
              { value: '12m', label: '12 Months' },
            ].map(p => (
              <button
                key={p.value}
                style={{ ...s.periodBtn, ...(period === p.value ? s.periodBtnActive : {}) }}
                onClick={() => setPeriod(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={s.loadingWrap}>
            <div style={s.spinner} />
            <p style={s.loadingText}>Loading analytics...</p>
          </div>
        ) : (
          <>
            {/* Overview Metrics */}
            {overview && (
              <div style={s.metricsGrid}>
                {[
                  { label: 'Assessments Completed', value: overview.submissions.total, growth: overview.submissions.growth, icon: '📋', color: '#1B9AAA' },
                  { label: 'Revenue Generated', value: `$${overview.revenue.total.toFixed(2)}`, growth: overview.revenue.growth, icon: '💰', color: '#27AE60' },
                  { label: 'New Users', value: overview.users.total, growth: overview.users.growth, icon: '👥', color: '#0A2342' },
                  { label: 'Conversion Rate', value: `${overview.conversionRate}%`, icon: '🎯', color: '#F39C12' },
                  { label: 'Pending Reviews', value: overview.pendingReports, icon: '⏳', color: overview.pendingReports > 0 ? '#E74C3C' : '#27AE60' },
                  { label: 'Upcoming Meetings', value: overview.upcomingMeetings, icon: '📅', color: '#9B59B6' },
                  { label: 'Feedback Responses', value: overview.feedback?.total ?? 0, growth: overview.feedback?.growth, icon: '💬', color: '#8E44AD' },
                ].map((m, i) => (
                  <div key={i} style={s.metricCard}>
                    <div style={s.metricIcon}>{m.icon}</div>
                    <div style={{ ...s.metricValue, color: m.color }}>{m.value}</div>
                    <div style={s.metricLabel}>{m.label}</div>
                    {m.growth !== undefined && <GrowthBadge value={m.growth} />}
                  </div>
                ))}
              </div>
            )}

            {/* Submission Trends Chart */}
            {submissions.length > 0 && (
              <div style={s.chartCard}>
                <h2 style={s.chartTitle}>Assessment Submissions Over Time</h2>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={submissions}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#1B9AAA" strokeWidth={2} dot={false} name="Submissions" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Revenue Chart */}
            {revenue.length > 0 && (
              <div style={s.chartCard}>
                <h2 style={s.chartTitle}>Monthly Revenue</h2>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={revenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                    <Tooltip formatter={v => [`$${v}`, 'Revenue']} />
                    <Bar dataKey="revenue" fill="#1B9AAA" radius={[4, 4, 0, 0]} name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Breakdown Charts */}
            {breakdown && (
              <div style={s.twoCharts}>
                <div style={s.chartCard}>
                  <h2 style={s.chartTitle}>Users by Type</h2>
                  {breakdown.users.every(u => u.value === 0) ? (
                    <p style={s.noData}>No data yet</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={breakdown.users} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
                          {breakdown.users.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div style={s.chartCard}>
                  <h2 style={s.chartTitle}>Revenue by Type</h2>
                  {breakdown.revenue.every(r => r.value === 0) ? (
                    <p style={s.noData}>No revenue yet</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={breakdown.revenue} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: $${value}`}>
                          {breakdown.revenue.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={v => `$${v}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            )}

            {/* Dimension Heatmap */}
            {dimensions.length > 0 && (
              <div style={s.chartCard}>
                <h2 style={s.chartTitle}>Dimension Analysis</h2>
                <p style={s.chartSubtitle}>Average scores and potential derailer rates across all assessments</p>
                <div style={s.dimensionTable}>
                  <div style={s.dimTableHead}>
                    <span>Dimension</span>
                    <span style={{ textAlign: 'center' }}>Avg Left Score</span>
                    <span style={{ textAlign: 'center' }}>Avg Right Score</span>
                    <span style={{ textAlign: 'center' }}>Derailer Rate</span>
                    <span style={{ textAlign: 'center' }}>Tension Rate</span>
                  </div>
                  {dimensions.sort((a, b) => b.blindSpotRate - a.blindSpotRate).map((dim, i) => (
                    <div key={i} style={s.dimTableRow}>
                      <div>
                        <div style={s.dimName}>{dim.dimensionId?.replace(/([A-Z])/g, ' $1').trim()}</div>
                        <div style={s.dimPoles}>{dim.leftPole} ↔ {dim.rightPole}</div>
                      </div>
                      <div style={s.dimScore}>
                        <span style={{ color: dim.avgLeft >= 16 ? '#27AE60' : '#E74C3C', fontWeight: '700' }}>
                          {dim.avgLeft}/24
                        </span>
                      </div>
                      <div style={s.dimScore}>
                        <span style={{ color: dim.avgRight >= 16 ? '#27AE60' : '#E74C3C', fontWeight: '700' }}>
                          {dim.avgRight}/24
                        </span>
                      </div>
                      <div style={s.dimScore}>
                        <div style={s.heatBar}>
                          <div style={{
                            ...s.heatFill,
                            width: `${dim.blindSpotRate}%`,
                            backgroundColor: dim.blindSpotRate > 50 ? '#E74C3C' : dim.blindSpotRate > 25 ? '#F39C12' : '#27AE60',
                          }} />
                        </div>
                        <span style={s.heatLabel}>{dim.blindSpotRate}%</span>
                      </div>
                      <div style={s.dimScore}>
                        <div style={s.heatBar}>
                          <div style={{
                            ...s.heatFill,
                            width: `${dim.tensionRate}%`,
                            backgroundColor: dim.tensionRate > 40 ? '#E74C3C' : dim.tensionRate > 20 ? '#F39C12' : '#27AE60',
                          }} />
                        </div>
                        <span style={s.heatLabel}>{dim.tensionRate}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Enterprise Completion */}
            {enterprise.length > 0 && (
              <div style={s.chartCard}>
                <h2 style={s.chartTitle}>Enterprise Completion Rates</h2>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={enterprise} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} />
                    <YAxis dataKey="companyName" type="category" tick={{ fontSize: 11 }} width={120} />
                    <Tooltip formatter={v => [`${v}%`, 'Completion Rate']} />
                    <Bar dataKey="completionRate" fill="#0A2342" radius={[0, 4, 4, 0]} name="Completion Rate" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Empty state */}
            {!overview?.submissions?.total && submissions.length === 0 && (
              <div style={s.empty}>
                <div style={s.emptyIcon}>📈</div>
                <p style={s.emptyTitle}>No data yet</p>
                <p style={s.emptyText}>Analytics will populate as users complete assessments and make payments.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-primary)' },
  sidebar: { width: '240px', backgroundColor: 'var(--color-primary)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarLogo: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  nav: { flex: 1, padding: '16px 0' },
  navItem: { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer', textAlign: 'left' },
  navActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  adminName: { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '10px' },
  logoutBtn: { width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer' },
  main: { flex: 1, backgroundColor: 'var(--color-dashboard-bg)', padding: '32px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title: { fontSize: '26px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: 'var(--color-text-light)' },
  periodSelector: { display: 'flex', gap: '6px', backgroundColor: '#fff', padding: '4px', borderRadius: '10px', border: '1px solid var(--color-border)' },
  periodBtn: { padding: '7px 14px', borderRadius: '7px', border: 'none', backgroundColor: 'transparent', color: 'var(--color-text-light)', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
  periodBtnActive: { backgroundColor: 'var(--color-teal)', color: '#fff' },
  loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px', gap: '16px' },
  spinner: { width: '40px', height: '40px', border: '4px solid var(--color-light-gray)', borderTopColor: 'var(--color-teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText: { fontSize: '14px', color: 'var(--color-text-light)' },
  metricsGrid: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginBottom: '20px' },
  metricCard: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '16px', boxShadow: 'var(--shadow-sm)' },
  metricIcon: { fontSize: '22px', marginBottom: '6px' },
  metricValue: { fontSize: '24px', fontWeight: '700', lineHeight: 1, marginBottom: '4px' },
  metricLabel: { fontSize: '11px', color: 'var(--color-text-light)', marginBottom: '6px', lineHeight: '1.3' },
  chartCard: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '24px', marginBottom: '16px', boxShadow: 'var(--shadow-sm)' },
  chartTitle: { fontSize: '16px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '4px' },
  chartSubtitle: { fontSize: '13px', color: 'var(--color-text-light)', marginBottom: '16px' },
  noData: { textAlign: 'center', color: 'var(--color-text-light)', padding: '40px', fontSize: '14px' },
  twoCharts: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
  dimensionTable: { display: 'flex', flexDirection: 'column', gap: '4px' },
  dimTableHead: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.2fr 1.2fr', gap: '12px', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid var(--color-border)', marginBottom: '4px' },
  dimTableRow: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1.2fr 1.2fr', gap: '12px', padding: '12px', borderRadius: '8px', backgroundColor: 'var(--color-off-white)', alignItems: 'center' },
  dimName: { fontSize: '13px', fontWeight: '600', color: 'var(--color-primary)', textTransform: 'capitalize' },
  dimPoles: { fontSize: '11px', color: 'var(--color-text-light)' },
  dimScore: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' },
  heatBar: { width: '80px', height: '6px', backgroundColor: 'var(--color-light-gray)', borderRadius: '100px', overflow: 'hidden' },
  heatFill: { height: '100%', borderRadius: '100px', transition: 'width 0.3s' },
  heatLabel: { fontSize: '12px', fontWeight: '600', color: 'var(--color-text)' },
  empty: { padding: '60px 20px', textAlign: 'center', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: 'var(--color-text-light)' },
};

export default Analytics;
